'use client';

import { c as _c } from "react/compiler-runtime";
import { PanelLeftOpen } from 'lucide-react';
import type { Route } from 'next';
import { usePathname } from 'next/navigation';
import { createContext, use, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import type { PrRef } from './session-panel/pr-ref';
import { formatPrRef } from './session-panel/pr-ref';
import { Button } from '@/components/ui/button';
import { KeyboardShortcut } from '@/components/ui/keyboard-shortcut';
import { BetterTooltip } from '@/components/ui/tooltip';
import { metaChar } from '@/lib/utils';
export const CODE_SIDEBAR_DEFAULT_WIDTH = 272;
export const CODE_SIDEBAR_MIN_WIDTH = 200;
export const CODE_SIDEBAR_MAX_WIDTH = 400;
export function codeSessionHref(sessionId: string): Route {
  return `/code/${sessionId}` as Route;
}

/** The landing for a new session, open on a repository when one is named. */
export function newCodeSessionHref(repo: string | null): Route {
  return (repo === null ? '/code' : `/code?repo=${encodeURIComponent(repo)}`) as Route;
}

/** A session opened with its side panel on the given pull request tab. */
export function codeSessionPrHref(sessionId: string, prRef: PrRef): Route {
  return `/code/${sessionId}?tab=pr&pr=${encodeURIComponent(formatPrRef(prRef))}` as Route;
}
type CodeShellContextValue = {
  isMobile: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  sidebarWidth: number;
  setSidebarWidth: (width: number) => void;
};
const CodeShellContext = createContext<CodeShellContextValue | null>(null);
function clampSidebarWidth(width: number): number {
  return Math.min(CODE_SIDEBAR_MAX_WIDTH, Math.max(CODE_SIDEBAR_MIN_WIDTH, Math.round(width)));
}
export function CodeShell(t0) {
  const $ = _c(19);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      initializeWithValue: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isMobile = useMediaQuery("(max-width: 767px)", t1);
  const pathname = usePathname();
  const viewportRef = useRef(null);
  let t2;
  let t3;
  if ($[1] !== isMobile) {
    t2 = () => {
      const layout = viewportRef.current?.closest("#home-layout");
      const pageScroll = layout?.closest("main");
      const viewport = window.visualViewport;
      if (!layout || !pageScroll) {
        return;
      }
      const updateViewport = () => {
        const active = !isMobile || !viewport || viewport.scale === 1;
        layout.toggleAttribute("data-code-active", active);
        pageScroll.toggleAttribute("data-code-active", active);
        if (isMobile && viewport && active) {
          layout.style.setProperty("--code-viewport-height", `${viewport.height}px`);
          layout.style.setProperty("--code-viewport-top", `${viewport.offsetTop}px`);
        } else {
          layout.style.removeProperty("--code-viewport-height");
          layout.style.removeProperty("--code-viewport-top");
        }
      };
      updateViewport();
      if (isMobile) {
        viewport?.addEventListener("resize", updateViewport);
        viewport?.addEventListener("scroll", updateViewport);
      }
      return () => {
        viewport?.removeEventListener("resize", updateViewport);
        viewport?.removeEventListener("scroll", updateViewport);
        layout.removeAttribute("data-code-active");
        pageScroll.removeAttribute("data-code-active");
        layout.style.removeProperty("--code-viewport-height");
        layout.style.removeProperty("--code-viewport-top");
      };
    };
    t3 = [isMobile];
    $[1] = isMobile;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useLayoutEffect(t2, t3);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const sidebarOpen = isMobile ? mobileSidebarOpen : desktopSidebarOpen;
  const setSidebarOpen = isMobile ? setMobileSidebarOpen : setDesktopSidebarOpen;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setMobileSidebarOpen(false);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isMobile || $[6] !== pathname) {
    t5 = [pathname, isMobile];
    $[5] = isMobile;
    $[6] = pathname;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useEffect(t4, t5);
  const [sidebarWidth, setSidebarWidthState] = useState(CODE_SIDEBAR_DEFAULT_WIDTH);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = width => {
      setSidebarWidthState(clampSidebarWidth(width));
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const setSidebarWidth = t6;
  let t7;
  if ($[9] !== isMobile || $[10] !== setSidebarOpen || $[11] !== sidebarOpen || $[12] !== sidebarWidth) {
    t7 = {
      isMobile,
      sidebarOpen,
      setSidebarOpen,
      sidebarWidth,
      setSidebarWidth
    };
    $[9] = isMobile;
    $[10] = setSidebarOpen;
    $[11] = sidebarOpen;
    $[12] = sidebarWidth;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const value = t7;
  let t8;
  if ($[14] !== children) {
    t8 = <div ref={viewportRef} data-code-viewport={true} className="h-full min-h-0">{children}</div>;
    $[14] = children;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8 || $[17] !== value) {
    t9 = <CodeShellContext value={value}>{t8}</CodeShellContext>;
    $[16] = t8;
    $[17] = value;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}
export function useCodeShell(): CodeShellContextValue {
  const context = use(CodeShellContext);
  if (!context) {
    throw new Error('useCodeShell must be used within CodeShell');
  }
  return context;
}

/** Renders only while the session list is collapsed, so the main pane can open it again. */
export function OpenCodeSidebarButton() {
  const $ = _c(6);
  const {
    sidebarOpen,
    setSidebarOpen
  } = useCodeShell();
  if (sidebarOpen) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="flex items-center gap-2">Open sidebar<KeyboardShortcut>{`${metaChar()}B`}</KeyboardShortcut></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== setSidebarOpen) {
    t1 = () => setSidebarOpen(true);
    $[1] = setSidebarOpen;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PanelLeftOpen aria-hidden={true} />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <BetterTooltip content={t0}><Button type="button" variant="ghost" size="icon" className="size-11 md:size-8 shrink-0 text-zinc-500 hover:bg-zinc-800/60 hover:text-zinc-200 [&_svg]:size-4" aria-label="Open sidebar" aria-keyshortcuts="Control+B Meta+B" onClick={t1}>{t2}</Button></BetterTooltip>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}